import type { AssetId } from '../types/ids';

/**
 * Theme asset IDs keep a trailing `.v[n]` token and carry no game prefix inside a theme
 * package (D-014) — e.g. `ar.player-ring.hero-1.v1`, never `spider-math.ar.…`.
 *
 * Segments are lowercase alphanumerics with single hyphens; at least two segments precede
 * the version token, so the first segment can act as the asset category.
 */
const SEGMENT = '[a-z0-9]+(?:-[a-z0-9]+)*';

export const ASSET_ID_PATTERN = new RegExp(`^${SEGMENT}(?:\\.${SEGMENT})+\\.v([1-9][0-9]*)$`);

export function isValidAssetId(value: string): value is AssetId {
  return ASSET_ID_PATTERN.test(value);
}

/** The numeric version encoded in the trailing `.v[n]` token, or null if the ID is invalid. */
export function assetIdVersion(value: string): number | null {
  const match = ASSET_ID_PATTERN.exec(value);
  if (match === null) return null;
  const digits = match[1];
  if (digits === undefined) return null;
  return Number.parseInt(digits, 10);
}

/**
 * First segment of an asset ID, used to pick a category placeholder when an ID does not
 * resolve. Returns null for IDs with no usable prefix.
 */
export function assetIdPrefix(value: string): string | null {
  const [prefix] = value.split('.');
  if (prefix === undefined || prefix.length === 0) return null;
  return prefix;
}
